import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Radio } from "lucide-react";
import { YT_EMBED_PARAMS } from "./YouTubeGuidePlayer";

const ytId = (url) => url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/)|youtu\.be\/)([\w-]{6,})/)?.[1];
const driveId = (url) => (url.match(/drive\.google\.com\/file\/d\/([\w-]+)/) || url.match(/drive\.google\.com\/(?:open|uc)\?(?:.*&)?id=([\w-]+)/))?.[1];

const LiveBadge = ({ offset }) => {
  const m = Math.floor(offset / 60), s = Math.floor(offset % 60);
  return (
    <div data-testid="live-badge" className="absolute left-3 top-3 z-20 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-[11px] font-medium text-white">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" /></span>
      LIVE · {String(Math.floor(m / 60)).padStart(2, "0")}:{String(m % 60).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </div>
  );
};

const JoinOverlay = ({ onJoin }) => (
  <button type="button" data-testid="live-join-btn" onClick={onJoin} className="absolute inset-0 z-20 grid place-items-center bg-[#1A2E26]/85 text-white">
    <span className="flex flex-col items-center gap-3">
      <span className="grid place-items-center w-20 h-20 rounded-full bg-white/15 border border-white/40 backdrop-blur"><Radio size={40} /></span>
      <span className="font-serif text-xl">Tap to join the class in progress</span>
      <span className="text-xs text-white/70">You will join at the same point as everyone else · no pause, rewind or fast-forward</span>
    </span>
  </button>
);

export const FinishedOverlay = ({ title }) => (
  <div data-testid="live-finished" className="w-full aspect-video rounded-2xl border border-border bg-[#1A2E26] text-white grid place-items-center text-center px-6">
    <span className="flex flex-col items-center gap-2"><CheckCircle2 size={40} className="text-emerald-300" /><span className="font-serif text-xl">{title || "Today's class has finished"}</span><span className="text-xs text-white/70">The next session starts at the scheduled time — see the countdown below.</span></span>
  </div>
);

// Clock shared by every viewer: seconds since the From time, based on the server clock.
const useLiveClock = (elapsedS, serverNow) => {
  const base = useRef({ elapsed: elapsedS || 0, at: Date.now(), skew: serverNow ? new Date(serverNow).getTime() - Date.now() : 0 });
  return useCallback(() => base.current.elapsed + (Date.now() - base.current.at) / 1000, []);
};

const Html5Live = ({ url, title, offsetAt, onDuration, onEnded, onProgress }) => {
  const ref = useRef(null);
  const [needTap, setNeedTap] = useState(false);
  const sync = () => { const v = ref.current; if (!v || !v.duration) return; const target = offsetAt(); if (target >= v.duration) { onEnded?.(v.duration); return; } if (Math.abs(v.currentTime - target) > 2.5) v.currentTime = target; };
  const tryPlay = () => ref.current?.play().then(() => setNeedTap(false)).catch(() => setNeedTap(true));
  const onMeta = () => { onDuration?.(ref.current.duration); onProgress?.(0, ref.current.duration); sync(); tryPlay(); };
  const onTime = () => { sync(); onProgress?.(ref.current.currentTime, ref.current.duration); };
  const onPause = () => { const v = ref.current; if (v && !v.ended) { sync(); tryPlay(); } };
  return (
    <div className="relative w-full aspect-video rounded-2xl border border-border bg-black overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
      <LiveBadge offset={offsetAt()} />
      <video ref={ref} data-testid="classroom-player" src={url} playsInline autoPlay controls={false} controlsList="nodownload noplaybackrate noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={onMeta} onTimeUpdate={onTime} onSeeking={sync} onPause={onPause} onEnded={() => { onProgress?.(ref.current?.duration, ref.current?.duration, true); onEnded?.(ref.current?.duration); }} className="w-full h-full pointer-events-none" />
      {needTap && <JoinOverlay onJoin={tryPlay} />}
    </div>
  );
};

const YouTubeLive = ({ id, title, offsetAt, onDuration, onEnded, onProgress }) => {
  const ref = useRef(null);
  const dur = useRef(0);
  const playing = useRef(false);
  const cb = useRef({ offsetAt, onDuration, onEnded, onProgress });
  cb.current = { offsetAt, onDuration, onEnded, onProgress };
  const [needTap, setNeedTap] = useState(false);
  const post = useCallback((func, args = []) => ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*"), []);
  const syncAndPlay = useCallback(() => {
    const target = cb.current.offsetAt();
    if (dur.current && target >= dur.current) { cb.current.onEnded?.(dur.current); return; }
    post("seekTo", [target, true]); post("playVideo");
  }, [post]);
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.origin.includes("youtube.com")) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d?.event === "onReady") { post("unloadModule", ["captions"]); post("unloadModule", ["cc"]); syncAndPlay(); }
        if (d?.event === "infoDelivery" && d.info) {
          if (d.info.duration && !dur.current) { dur.current = d.info.duration; cb.current.onDuration?.(d.info.duration); cb.current.onProgress?.(0, d.info.duration); if (cb.current.offsetAt() >= dur.current) cb.current.onEnded?.(dur.current); }
          if (d.info.currentTime != null && playing.current) {
            const target = cb.current.offsetAt();
            if (Math.abs(d.info.currentTime - target) > 3) post("seekTo", [target, true]);
            cb.current.onProgress?.(d.info.currentTime, dur.current);
          }
        }
        if (d?.event === "onStateChange") {
          if (d.info === 1) { playing.current = true; setNeedTap(false); }
          if (d.info === 2 && playing.current) syncAndPlay();
          if (d.info === 0) { playing.current = false; cb.current.onProgress?.(dur.current, dur.current, true); cb.current.onEnded?.(dur.current); }
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    const t = setTimeout(() => { if (!playing.current) setNeedTap(true); }, 3500);
    return () => { window.removeEventListener("message", onMsg); clearTimeout(t); };
  }, [post, syncAndPlay]);
  const onLoad = () => { ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1 }), "*"); setTimeout(syncAndPlay, 600); };
  return (
    <div className="relative w-full aspect-video rounded-2xl border border-border bg-black overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
      <LiveBadge offset={offsetAt()} />
      <iframe ref={ref} data-testid="classroom-player" title={title} onLoad={onLoad} src={`https://www.youtube.com/embed/${id}?${YT_EMBED_PARAMS}&autoplay=1&origin=${encodeURIComponent(window.location.origin)}`} className="absolute left-0 w-full h-[calc(100%+140px)] -top-[70px] pointer-events-none" allow="autoplay; encrypted-media" />
      <div className="absolute inset-0 z-10" />
      {needTap && <JoinOverlay onJoin={() => { setNeedTap(false); syncAndPlay(); }} />}
    </div>
  );
};

const DriveLive = ({ id, title }) => (
  <div className="relative w-full aspect-video rounded-2xl border border-border bg-black overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
    <div data-testid="player-top-guard" className="absolute inset-x-0 top-0 h-16 z-10" /><div data-testid="player-corner-guard" className="absolute right-0 bottom-0 w-24 h-14 z-10" />
    <iframe data-testid="classroom-player" title={title} src={`https://drive.google.com/file/d/${id}/preview`} className="absolute left-0 w-full h-[calc(100%+56px)] -top-[56px]" allow="autoplay" />
  </div>
);

// Plays the day's video(s) as a synchronised broadcast starting from the From time.
export const LivePlayer = ({ playlist, elapsedS, serverNow, onProgress, onFinished }) => {
  const clock = useLiveClock(elapsedS, serverNow);
  const durs = useRef(Object.fromEntries(playlist.map((p) => [p.key, Number(p.duration_s) || 0])));
  const [idx, setIdx] = useState(() => {
    let off = elapsedS || 0, i = 0;
    while (i < playlist.length && durs.current[playlist[i].key] && off >= durs.current[playlist[i].key]) { off -= durs.current[playlist[i].key]; i += 1; }
    return i;
  });
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(t); }, []);
  const before = playlist.slice(0, idx).reduce((a, p) => a + (durs.current[p.key] || 0), 0);
  const offsetAt = useCallback(() => Math.max(0, clock() - before), [clock, before]);
  const current = playlist[idx];
  const endedKeys = useRef(new Set());
  const advance = useCallback((d) => { if (!current || endedKeys.current.has(current.key)) return; endedKeys.current.add(current.key); if (d) durs.current[current.key] = d; setIdx((i) => i + 1); }, [current]);
  const onDuration = useCallback((d) => { if (current && d) durs.current[current.key] = d; }, [current]);
  useEffect(() => { if (idx >= playlist.length) onFinished?.(); }, [idx, playlist.length, onFinished]);
  if (!current) return <FinishedOverlay />;
  const report = (pos, dur, done) => onProgress?.(current.key, pos, dur, done);
  const yt = ytId(current.url), gd = driveId(current.url);
  const key = `${current.key}-${idx}`;
  return (
    <>
      {playlist.length > 1 && <p className="text-xs text-muted-foreground mb-2" data-testid="live-part">Now playing: <span className="font-medium text-foreground">{current.title}</span> ({idx + 1} of {playlist.length})</p>}
      {yt ? <YouTubeLive key={key} id={yt} title={current.title} offsetAt={offsetAt} onDuration={onDuration} onEnded={advance} onProgress={report} />
        : gd ? <DriveLive key={key} id={gd} title={current.title} />
          : <Html5Live key={key} url={current.url} title={current.title} offsetAt={offsetAt} onDuration={onDuration} onEnded={advance} onProgress={report} />}
    </>
  );
};
